// services/userService.js
import supabase from './supabase';

/**
 * Get current user profile
 */
export const getCurrentUserProfile = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', user.id)
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error getting user profile:', error);
    return null;
  }
};

/**
 * Get user profile by ID
 */
export const getUserProfile = async (userId) => {
  try {
    if (!userId) throw new Error('User ID is required');
    
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error getting user profile:', error);
    return null;
  }
};

/**
 * Update user profile
 */
export const updateUserProfile = async (userData) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');
    
    const updateData = {
      ...userData,
      updated_at: new Date().toISOString(),
    };
    
    const { data, error } = await supabase
      .from('users')
      .update(updateData)
      .eq('id', user.id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

/**
 * Upload profile photo
 */
export const uploadProfilePhoto = async (file) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');
    
    // Create unique filename
    const timestamp = Date.now();
    const filename = `${user.id}_${timestamp}.${file.name.split('.').pop()}`;
    const filePath = `profile-photos/${filename}`;
    
    // Upload to Supabase Storage
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('profile-media')
      .upload(filePath, file, {
        contentType: file.type,
        upsert: true,
      });
    
    if (uploadError) throw uploadError;
    
    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('profile-media')
      .getPublicUrl(filePath);
    
    // Update user profile with photo URL
    await updateUserProfile({ 
      profile_photo_cdn_url: publicUrl,
      profile_photo_path: filePath 
    });
    
    return publicUrl;
  } catch (error) {
    console.error('Error uploading profile photo:', error);
    throw error;
  }
};

/**
 * Delete profile photo
 */
export const deleteProfilePhoto = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');
    
    // Get current profile
    const profile = await getCurrentUserProfile();
    
    if (profile?.profile_photo_path) {
      // Delete from storage
      await supabase.storage
        .from('profile-media')
        .remove([profile.profile_photo_path]);
    }
    
    // Update profile to remove photo URLs
    await updateUserProfile({
      profile_photo_cdn_url: null,
      profile_photo_path: null,
    });
    
    return true;
  } catch (error) {
    console.error('Error deleting profile photo:', error);
    throw error;
  }
};